// Content log table — sortable list of every blog anchor + entry form.
// Replaces the BWM Content Manual Log Google Sheet entirely.
//
// Filters: pillar, status. Click a row to edit. Add Entry button opens
// the same form for a new entry.

import { useMemo, useState } from 'react';
import { useContent } from '../../context/ContentContext';
import { PILLARS, getPillar, getPillarLabel, CONTENT_STATUSES, STATUS_BY_ID } from './pillars';
import ContentEntryModal from './ContentEntryModal';
import TimeEntryModal from './TimeEntryModal';

const COLS = [
  { key: 'title',        label: 'Title',         width: '24%' },
  { key: 'pillar',       label: 'Pillar',        width: '16%' },
  { key: 'status',       label: 'Status',        width: '10%' },
  { key: 'publishDate',  label: 'Publish Date',  width: '10%' },
  { key: 'primaryKeyword', label: 'Primary KW',  width: '14%' },
  { key: 'kwMetrics',    label: 'Vol / KD',      width: '8%' },
  { key: 'wordCount',    label: 'Words',         width: '6%' },
  { key: 'links',        label: 'Links In/Out',  width: '8%' },
];

export default function ContentLogView() {
  const { entries, loading, error, removeOne } = useContent();
  const [pillarFilter, setPillarFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const filtered = useMemo(() => {
    return entries.filter(e => {
      if (pillarFilter && e.pillar !== pillarFilter) return false;
      if (statusFilter && e.status !== statusFilter) return false;
      return true;
    });
  }, [entries, pillarFilter, statusFilter]);

  function openAdd() { setEditing(null); setShowForm(true); }
  function openEdit(entry) { setEditing(entry); setShowForm(true); }
  function closeForm() { setEditing(null); setShowForm(false); }

  // Time entry modal — opened from the per-row clock button.
  const [timeEntry, setTimeEntry] = useState(null); // { id, title, status } or null
  function openTimeEntry(entry) { setTimeEntry(entry); }
  function closeTimeEntry() { setTimeEntry(null); }

  async function handleDelete(entry) {
    if (!window.confirm(`Delete "${entry.title}"? This can't be undone.`)) return;
    try { await removeOne(entry.id); } catch (e) { window.alert('Delete failed: ' + e.message); }
  }

  if (loading) return <div className="text-[12px] text-[#888] p-4">Loading content log...</div>;
  if (error) return <div className="text-[12px] text-rose p-4">Failed to load: {error}</div>;

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] px-4 py-2 flex items-center gap-3">
        <select
          value={pillarFilter}
          onChange={e => setPillarFilter(e.target.value)}
          className="px-2 py-1 text-[11px] font-bold rounded-[3px] bg-white text-navy border border-gray-200 cursor-pointer"
        >
          <option value="">All Pillars</option>
          {PILLARS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-2 py-1 text-[11px] font-bold rounded-[3px] bg-white text-navy border border-gray-200 cursor-pointer"
        >
          <option value="">All Statuses</option>
          {CONTENT_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <span className="text-[11px] text-[#888] ml-2">{filtered.length} of {entries.length}</span>
        <div className="flex-1" />
        <button
          onClick={openAdd}
          className="px-3 py-1.5 text-[11px] font-bold text-white bg-bullogic-blue rounded-[3px] hover:bg-bullogic-blue/90"
        >
          + Add Entry
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-[12px] text-[#888] mb-1">No entries yet.</div>
            <div className="text-[11px] text-[#aaa]">Click "+ Add Entry" to log your first blog anchor.</div>
          </div>
        ) : (
          <table className="w-full text-[12px] table-fixed">
            <colgroup>
              {COLS.map(c => <col key={c.key} style={{ width: c.width }} />)}
              <col style={{ width: '4%' }} />
            </colgroup>
            <thead className="bg-gray-50">
              <tr className="text-left">
                {COLS.map(c => (
                  <th key={c.key} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">{c.label}</th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const pillar = getPillar(e.pillar);
                const status = STATUS_BY_ID[e.status];
                return (
                  <tr
                    key={e.id}
                    onClick={() => openEdit(e)}
                    className="border-t border-gray-100 hover:bg-bone/30 cursor-pointer"
                  >
                    <td className="px-3 py-2 truncate font-bold text-navy" title={e.title}>{e.title}</td>
                    <td className="px-3 py-2 truncate">
                      {pillar ? (
                        <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: pillar.bg }} />
                      ) : null}
                      <span className="align-middle text-[#666]">{getPillarLabel(e.pillar)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {status && (
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[2px] ${status.color}`}>
                          {status.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#666]">{e.publishDate || '—'}</td>
                    <td className="px-3 py-2 truncate text-[#666] font-mono text-[11px]" title={e.primaryKeyword}>{e.primaryKeyword || '—'}</td>
                    <td className="px-3 py-2 text-[#666] tabular-nums text-[11px]">
                      {e.keywordVolume != null && e.keywordDifficulty != null
                        ? `${e.keywordVolume} / ${e.keywordDifficulty}`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-[#666] tabular-nums text-[11px]">{e.wordCount || '—'}</td>
                    <td className="px-3 py-2 text-[#666] tabular-nums text-[11px]">
                      {(e.internalLinksIn ?? 0)} / {(e.internalLinksOut ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={(ev) => { ev.stopPropagation(); openTimeEntry(e); }}
                        className="text-[#aaa] hover:text-bullogic-blue text-[12px] mr-2"
                        title="Log time on this content"
                      >
                        ⏱
                      </button>
                      <button
                        onClick={(ev) => { ev.stopPropagation(); handleDelete(e); }}
                        className="text-[#ccc] hover:text-rose text-[14px]"
                        title="Delete"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <ContentEntryModal entry={editing} onClose={closeForm} />
      )}

      {timeEntry && (
        <TimeEntryModal
          contentLogId={timeEntry.id}
          contentTitle={timeEntry.title}
          contentStatus={timeEntry.status}
          onClose={closeTimeEntry}
        />
      )}
    </div>
  );
}
